import React, { useState } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { getDashboardSummary } from "../../store/modules/transactions/transactionActions";
import { useQuery } from "@tanstack/react-query";
import { formatCurrencyForStats } from "../../utils/common";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";

interface TransactionSummaryContainerProps {
  yearId?: number;
}

// Mobile Card Component
const MobileCardView = ({
  summaries,
  sbSummaries,
  otherSummaries,
  totalUnits,
  totalVolume,
  totalAveragePrice,
  sbTotalUnits,
  sbTotalVolume,
  sbAveragePrice,
  formatCurrency,
  formatUnits,
}: {
  summaries: any[];
  sbSummaries: any[];
  otherSummaries: any[];
  totalUnits: number;
  totalVolume: number;
  totalAveragePrice: number;
  sbTotalUnits: number;
  sbTotalVolume: number;
  sbAveragePrice: number;
  formatCurrency: (value: number) => string;
  formatUnits: (value: number) => string;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Total Card */}
      <Card elevation={2} sx={{ backgroundColor: "primary.50" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Total
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Units
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatUnits(totalUnits)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Volume
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatCurrency(totalVolume)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" color="text.secondary">
                Avg Price
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {formatCurrency(totalAveragePrice)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* S&B Summary Card */}
      {sbSummaries.length > 0 && (
        <Card elevation={1}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              S&B (Sellers and Buyers)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Units
                </Typography>
                <Typography variant="body2">
                  {formatUnits(sbTotalUnits)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Volume
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(sbTotalVolume)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Avg Price
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(sbAveragePrice)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* S&B Detail Cards */}
      {sbSummaries.map((summary, index) => (
        <Card key={`sb-${index}`} elevation={1} sx={{ ml: 2 }}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {summary.type || "Unknown"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Units
                </Typography>
                <Typography variant="body2">
                  {formatUnits(summary.totalUnits || 0)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Volume
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(summary.totalVolume || 0)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Avg Price
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(summary.averagePrice || 0)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Other Summary Cards */}
      {otherSummaries.map((summary, index) => (
        <Card key={`other-${index}`} elevation={1}>
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              {summary.type || "Unknown"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Units
                </Typography>
                <Typography variant="body2">
                  {formatUnits(summary.totalUnits || 0)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Volume
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(summary.totalVolume || 0)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Avg Price
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(summary.averagePrice || 0)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

// Desktop Table Component
const DesktopTableView = ({
  summaries,
  sbSummaries,
  otherSummaries,
  totalUnits,
  totalVolume,
  totalAveragePrice,
  sbTotalUnits,
  sbTotalVolume,
  sbAveragePrice,
  formatCurrency,
  formatUnits,
}: {
  summaries: any[];
  sbSummaries: any[];
  otherSummaries: any[];
  totalUnits: number;
  totalVolume: number;
  totalAveragePrice: number;
  sbTotalUnits: number;
  sbTotalVolume: number;
  sbAveragePrice: number;
  formatCurrency: (value: number) => string;
  formatUnits: (value: number) => string;
}) => {
  return (
    <TableContainer
      component={Paper}
      elevation={1}
      sx={{
        overflowX: "auto",
        "& .MuiTable-root": {
          minWidth: "auto",
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "grey.50" }}>
            <TableCell
              sx={{
                fontWeight: 600,
                width: "40%",
                minWidth: 150,
                fontSize: "1rem",
              }}
            >
              Category
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                width: "20%",
                minWidth: 100,
                fontSize: "1rem",
              }}
            >
              Units
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                width: "20%",
                minWidth: 120,
                fontSize: "1rem",
              }}
            >
              Volume
            </TableCell>
            <TableCell
              align="right"
              sx={{
                fontWeight: 600,
                width: "20%",
                minWidth: 120,
                fontSize: "1rem",
              }}
            >
              Average Price
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Total Row */}
          <TableRow
            sx={{
              backgroundColor: "primary.50",
              "& td": {
                fontWeight: 600,
                fontSize: "1rem",
                py: 2,
              },
            }}
          >
            <TableCell>Total</TableCell>
            <TableCell align="right">{formatUnits(totalUnits)}</TableCell>
            <TableCell align="right">{formatCurrency(totalVolume)}</TableCell>
            <TableCell align="right">
              {formatCurrency(totalAveragePrice)}
            </TableCell>
          </TableRow>

          {/* S&B Summary Row (if there are S&B transactions) */}
          {sbSummaries.length > 0 && (
            <TableRow
              sx={{
                "& td": {
                  fontSize: "1rem",
                  py: 2,
                },
              }}
            >
              <TableCell>S&B (Sellers and Buyers)</TableCell>
              <TableCell align="right">{formatUnits(sbTotalUnits)}</TableCell>
              <TableCell align="right">
                {formatCurrency(sbTotalVolume)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(sbAveragePrice)}
              </TableCell>
            </TableRow>
          )}

          {sbSummaries.map((summary, index) => (
            <TableRow
              key={`sb-${index}`}
              sx={{
                "& td": {
                  fontSize: "1rem",
                  py: 2,
                },
              }}
            >
              <TableCell sx={{ pl: 4 }}>{summary.type || "Unknown"}</TableCell>
              <TableCell align="right">
                {formatUnits(summary.totalUnits || 0)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(summary.totalVolume || 0)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(summary.averagePrice || 0)}
              </TableCell>
            </TableRow>
          ))}

          {otherSummaries.map((summary, index) => (
            <TableRow
              key={`other-${index}`}
              sx={{
                "& td": {
                  fontSize: "1rem",
                  py: 2,
                },
              }}
            >
              <TableCell>{summary.type || "Unknown"}</TableCell>
              <TableCell align="right">
                {formatUnits(summary.totalUnits || 0)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(summary.totalVolume || 0)}
              </TableCell>
              <TableCell align="right">
                {formatCurrency(summary.averagePrice || 0)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TransactionSummaryContainer: React.FC<
  TransactionSummaryContainerProps
> = ({ yearId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State for collapsing the entire summary
  const [isExpanded, setIsExpanded] = useState(false);

  // Get co-agent filter from Redux
  const { selectedCoAgentIds } = useSelector(
    (state: RootState) => state.coAgentFilter,
  );

  // const [summaries, setSummaries] = useState<ITransactionSummary[]>([]);
  const { data: summaries = [] } = useQuery({
    queryKey: ["dashboardSummary", yearId, selectedCoAgentIds],
    queryFn: () => getDashboardSummary(yearId),
    refetchOnWindowFocus: false,
  });

  if (summaries.length === 0) {
    return null;
  }

  // Separate S&B transactions from other types
  const sbSummaries = summaries.filter((summary) => summary.isSb === true);
  const otherSummaries = summaries.filter((summary) => summary.isSb !== true);

  const totalUnits = summaries.reduce(
    (sum, summary) => sum + (summary.totalUnits || 0),
    0,
  );
  const totalVolume = summaries.reduce(
    (sum, summary) => sum + (summary.totalVolume || 0),
    0,
  );
  const totalAveragePrice = totalUnits > 0 ? totalVolume / totalUnits : 0;

  const sbTotalUnits = sbSummaries.reduce(
    (sum, summary) => sum + (summary.totalUnits || 0),
    0,
  );
  const sbTotalVolume = sbSummaries.reduce(
    (sum, summary) => sum + (summary.totalVolume || 0),
    0,
  );
  const sbAveragePrice = sbTotalUnits > 0 ? sbTotalVolume / sbTotalUnits : 0;

  const formatCurrency = (value: number) => {
    return formatCurrencyForStats(value);
  };

  const formatUnits = (value: number) => {
    return value.toString();
  };

  return (
    <Paper elevation={2} sx={{ mb: 3 }}>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton size="small" sx={{ mr: 1 }}>
            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
          </IconButton>
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
          >
            Transactions Summary
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontStyle: "italic",
            opacity: 0.7,
            transition: "opacity 0.2s ease-in-out",
            display: { xs: "none", sm: "block" },
          }}
        >
          Click to {isExpanded ? "collapse" : "expand"}
        </Typography>
      </Box>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 3 }}>
          {isMobile ? (
            <MobileCardView
              summaries={summaries}
              sbSummaries={sbSummaries}
              otherSummaries={otherSummaries}
              totalUnits={totalUnits}
              totalVolume={totalVolume}
              totalAveragePrice={totalAveragePrice}
              sbTotalUnits={sbTotalUnits}
              sbTotalVolume={sbTotalVolume}
              sbAveragePrice={sbAveragePrice}
              formatCurrency={formatCurrency}
              formatUnits={formatUnits}
            />
          ) : (
            <DesktopTableView
              summaries={summaries}
              sbSummaries={sbSummaries}
              otherSummaries={otherSummaries}
              totalUnits={totalUnits}
              totalVolume={totalVolume}
              totalAveragePrice={totalAveragePrice}
              sbTotalUnits={sbTotalUnits}
              sbTotalVolume={sbTotalVolume}
              sbAveragePrice={sbAveragePrice}
              formatCurrency={formatCurrency}
              formatUnits={formatUnits}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TransactionSummaryContainer;
